import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mic, Send, Calendar, BarChart3, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { ProductMock } from "@/components/marketing/ProductMock";
import { usePromo } from "@/hooks/usePromo";

const capabilities = [
  { icon: Mic, title: "Voice-to-CRM", body: "Speak between calls. AI captures the lead, the value, and the next step." },
  { icon: Send, title: "Telegram-native", body: "Add and update leads from any chat. No app switching." },
  { icon: BarChart3, title: "Visual timelines", body: "Drag deals across a timeline. Export to PDF when you need to." },
  { icon: Calendar, title: "Calendar sync", body: "Two-way Google Calendar sync, with timezone-aware scheduling." },
  { icon: Users, title: "Team workspaces", body: "Personal pipeline plus shared group workspaces with roles." },
  { icon: Sparkles, title: "AI lead parsing", body: "Paste a transcript or note. The structure extracts itself." },
];

const Landing = () => {
  const navigate = useNavigate();
  const { isPromoActive, offer } = usePromo();


  return (
    <>
      <Helmet>
        <title>EchoCRM, The CRM for sales reps who talk more than they type</title>
        <meta name="description" content="EchoCRM turns voice notes, Telegram messages, and calls into a working pipeline. Built for high-volume sales teams." />
        <link rel="canonical" href="https://echocrm.app/" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground antialiased">
        <MarketingNav />

        {/* Hero, left aligned, single claim, single CTA */}
        <section className="relative theme-glow">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-20">
            <div className="max-w-3xl lg:max-w-4xl">
              <p className="text-[12px] sm:text-[13px] font-mono-accent uppercase tracking-[0.18em] text-primary mb-5">
                7-day free trial · Cancel anytime
              </p>
              {isPromoActive && offer && (
                <p className="-mt-2 mb-6 inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[13px] text-primary">
                  {offer.headline}
                </p>
              )}

              <h1 className="font-display font-semibold text-[40px] leading-[1.05] sm:text-[64px] lg:text-[80px] tracking-[-0.035em]">
                The CRM that keeps up<br className="hidden sm:block" /> with the conversation.
              </h1>
              <p className="mt-6 sm:mt-7 text-[16px] sm:text-[18px] leading-[1.55] text-muted-foreground max-w-2xl">
                EchoCRM turns voice notes, Telegram messages, and call recaps into a clean pipeline, so reps spend their day closing, not typing.
              </p>
              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                  Start free trial
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Link
                  to="/features"
                  className="text-[14px] text-muted-foreground hover:text-foreground inline-flex items-center justify-center sm:justify-start gap-1.5 py-2"
                >
                  See how it works <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Real product UI directly under the hero */}
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pb-20 sm:pb-28">
            <ProductMock />
          </div>
        </section>

        {/* Capabilities, restrained list */}
        <section className="hairline-t">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
            <div className="max-w-2xl mb-12 sm:mb-16">
              <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">Capabilities</p>
              <h2 className="font-display font-semibold text-[28px] sm:text-[40px] lg:text-[48px] tracking-[-0.03em] leading-[1.05]">
                Designed around the way reps actually work.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {capabilities.map((c, i) => (
                <div
                  key={c.title}
                  className={`p-6 lg:p-8 border-border ${
                    i % 1 === 0 ? "border-t" : ""
                  } ${i % 3 !== 0 ? "lg:border-l" : ""} ${i % 2 !== 0 ? "md:border-l lg:border-l" : ""}`}
                >
                  <c.icon className="h-4 w-4 text-primary mb-4" />
                  <h3 className="font-display font-semibold text-[16px] mb-1.5">{c.title}</h3>
                  <p className="text-[14px] text-muted-foreground leading-[1.55]">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote / belief */}
        <section className="hairline-t">
          <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 sm:py-28">
            <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-6">Why EchoCRM</p>
            <p className="font-display text-[22px] sm:text-[28px] lg:text-[32px] leading-[1.3] tracking-[-0.02em] text-foreground">
              Most CRMs were built for managers reviewing pipelines.
              EchoCRM is built for the rep with a phone in one hand and a coffee in the other,
              between a discovery call and a site visit.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="hairline-t">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
            <div className="max-w-2xl">
              <h2 className="font-display font-semibold text-[28px] sm:text-[40px] tracking-[-0.03em] leading-[1.1]">
                Stop typing.<br />Start closing.
              </h2>
              <p className="mt-5 text-[16px] text-muted-foreground max-w-lg">
                Spin up a workspace in under a minute. Bring your team when you're ready.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-5">
                <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                  Start 7-day free trial
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Link to="/pricing" className="text-[14px] text-muted-foreground hover:text-foreground inline-flex items-center justify-center sm:justify-start gap-1.5 py-2">
                  View pricing <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </main>
    </>
  );
};

export default Landing;